import { useEffect, useRef, useState } from 'react';

export default function HeroSection() {
  const [scrollY, setScrollY] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 300);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  const titleOpacity = Math.max(0, 1 - scrollY / 500);
  const titleY = scrollY * 0.3;
  const imageY = scrollY * 0.15;
  const imageScale = 1 + scrollY * 0.0003;
  const imageRotate = scrollY * 0.02;

  return (
    <section ref={sectionRef} className="relative w-full h-screen bg-black" style={{ overflow: 'hidden' }}>
      {/* Animated computer image — floating bubble */}
      <div
        className="absolute"
        style={{
          right: '-2%',
          bottom: '8%',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 1s ease-out',
          animation: 'bubbleFloat 18s ease-in-out infinite',
          zIndex: 5,
        }}
      >
        <div className="relative">
          <img
            src="https://public.readdy.ai/ai/img_res/45c88a99-b77a-42c6-9ac4-ceabb0a1bc65.png"
            alt="Syntera computer"
            className="w-auto h-auto max-w-[32vw] max-h-[42vh] object-contain"
            style={{
              filter: 'drop-shadow(0 0 60px rgba(250, 110, 79, 0.3)) drop-shadow(0 0 120px rgba(142, 201, 187, 0.15))',
            }}
          />
          {/* Glow orb behind */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              width: '140%',
              height: '140%',
              background: 'radial-gradient(circle, rgba(250,110,79,0.12) 0%, rgba(142,201,187,0.06) 40%, transparent 70%)',
              animation: 'glowPulse 4s ease-in-out infinite',
              zIndex: -1,
            }}
          />
        </div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[
          { x: '15%', y: '20%', size: 4, delay: 0, color: '#FA6E4F' },
          { x: '80%', y: '15%', size: 3, delay: 1.2, color: '#8EC9BB' },
          { x: '70%', y: '70%', size: 5, delay: 0.5, color: '#F2CF59' },
          { x: '25%', y: '75%', size: 3, delay: 2, color: '#FB8E7E' },
          { x: '50%', y: '10%', size: 2, delay: 0.8, color: '#FA6E4F' },
          { x: '90%', y: '50%', size: 4, delay: 1.5, color: '#8EC9BB' },
          { x: '10%', y: '45%', size: 3, delay: 0.3, color: '#F2CF59' },
          { x: '60%', y: '85%', size: 2, delay: 1.8, color: '#FB8E7E' },
        ].map((p, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              left: p.x,
              top: p.y,
              width: p.size,
              height: p.size,
              background: p.color,
              opacity: 0.4,
              animation: `particleFloat ${4 + i * 0.7}s ease-in-out infinite alternate`,
              animationDelay: `${p.delay}s`,
              boxShadow: `0 0 ${p.size * 3}px ${p.color}80`,
            }}
          />
        ))}
      </div>

      {/* Dark overlay for text contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80 pointer-events-none" />

      {/* Hero content */}
      <div
        className="absolute inset-0 z-20 flex flex-col justify-end pb-16 px-10 pointer-events-none"
        style={{ transform: `translateY(${titleY}px)`, opacity: titleOpacity }}
      >
        <div
          className={`transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`}
        >
          <p
            className="text-[#F8CA9D] font-black uppercase tracking-[0.5em] mb-12"
            style={{ fontSize: 'clamp(13px, 1.4vw, 22px)', letterSpacing: '0.45em' }}
          >
            &#8212;&nbsp;&nbsp;Software Development &amp; Digital Solutions&nbsp;&nbsp;&#8212;
          </p>
          <h1
            className="boldonse text-white leading-none"
            style={{ fontSize: 'clamp(70px, 12vw, 190px)', lineHeight: 0.88 }}
          >
            BUILD
            <br />
            <span style={{ color: '#FA6E4F' }}>DIGITAL</span>
          </h1>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2"
        style={{ opacity: titleOpacity }}
      >
        <span className="text-[#8EC9BB] text-xs tracking-[0.3em] uppercase">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-[#8EC9BB] to-transparent animate-pulse" />
      </div>

      <style>{`
        @keyframes bubbleFloat {
          0%   { transform: translateY(0px) translateX(0px) rotate(0deg); }
          15%  { transform: translateY(-40px) translateX(-30px) rotate(2deg); }
          30%  { transform: translateY(-20px) translateX(-60px) rotate(-1deg); }
          45%  { transform: translateY(-60px) translateX(-20px) rotate(3deg); }
          60%  { transform: translateY(-30px) translateX(20px) rotate(-2deg); }
          75%  { transform: translateY(-50px) translateX(50px) rotate(1deg); }
          90%  { transform: translateY(-10px) translateX(25px) rotate(-1deg); }
          100% { transform: translateY(0px) translateX(0px) rotate(0deg); }
        }
        @keyframes glowPulse {
          0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
        }
        @keyframes particleFloat {
          from { transform: translateY(0px) translateX(0px); opacity: 0.3; }
          to { transform: translateY(-30px) translateX(10px); opacity: 0.6; }
        }
      `}</style>
    </section>
  );
}